/**
 * 验收标准确认书 · 验收标准与合同 — the STAFF surface (估价/采购/GM) on PackageDetail's Standards tab.
 * 估价 assigns the PM after 议价→锁定; reviews the PM's submitted criteria (release / return-with-
 * comment — no edit rights); watches the Q&A; records the meeting when the PM can't; previews the
 * annexure + both confirmation badges; generates/downloads the contract .docx once finalized.
 */
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, execActions, getLatestVoteRound, voteBlocksInvite, type PackageDetail,
  getMe, listStaff, getStandardsWorkflow, standardsWorkflowActions, getWorkflowAnnexure,
  listContractGenerations, generateContract, listTemplatesForGeneration, annexureDownloadUrl, contractDownloadUrl,
  getPackageSubcontracts, WF_STATUS, apiErrText as errText, packageFileDownloadUrl, fileSizeText,
  staffUploadWorkflowDoc, staffDeleteWorkflowDoc, staffWorkflowDocUrl, rmbCents, TAX_PCT_MISSING_HINT,
  type MeResponse, type StaffRow, type SubcontractRow, type AnnexureView, type DocTemplate, type PackageFileMeta,
  type AmendContractValueResult,
} from '../lib/api';
import WorkflowTimeline from './WorkflowTimeline';
import RevertSignature from './RevertSignature';
import AmendContractValue, { AmendmentBreakdown, AwardVsContractLine, awardDiffers, canAmendContractValue } from './AmendContractValue';
import { toast } from './ui/Toast';
import MdView from './MdView';
import { Skeleton } from './ui/Skeleton';

/** 已发出 X 天 — how long the labor company has been sitting on the released standards. */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export default function StandardsWorkflowCard({ pid, isLocked }: { pid: string; isLocked: boolean }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const canManage = me?.account?.role === 'budget' || me?.account?.role === 'gm';
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ['standards-workflow', pid], queryFn: () => getStandardsWorkflow(pid) });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); }
    catch (e) { setErr(errText(e)); }
    finally {
      setBusy(false);
      // BOTH refetches belong in finally: a compose can succeed server-side and still fail the client
      // (proxy drop during the up-to-2-minute LLM round trip), and a 409 means the state moved
      // underneath us — either way the stale card must be refreshed, not frozen.
      await qc.invalidateQueries({ queryKey: ['standards-workflow', pid] });
      // the annexure lives in its OWN query (keyed by workflow id) — without this, composing the
      // annexure via 记录会议 leaves the AnnexureSection on its cached {current:null} until a hard refresh
      if (data?.workflow?.id) await qc.invalidateQueries({ queryKey: ['workflow-annexure', data.workflow.id] });
    }
  }

  if (isLoading) return <div className="card"><Skeleton lines={3} /></div>;
  // Without this the failed fetch left data===undefined and the card re-rendered 「指派项目经理」 —
  // the operator was shown an assign screen for a workflow that very much exists.
  if (isError) {
    return (
      <div className="card small" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
        验收标准信息加载失败：{errText(error)}
        <button style={{ marginLeft: 8 }} onClick={() => void refetch()}>重试 / retry</button>
      </div>
    );
  }
  const wf = data?.workflow ?? null;

  return (
    <div>
      {err && <div className="card small" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>{err}</div>}
      {!wf ? (
        <AssignCard isLocked={isLocked} canManage={!!canManage} busy={busy} onAssign={(sid) => act(() => standardsWorkflowActions.assign(pid, sid))} />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 10 }}>
            <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>验收标准确认 / Standards confirmation</h3>
              <span className={`pill small ${WF_STATUS[wf.status]?.pill}`}>{WF_STATUS[wf.status]?.label}</span>
              {data?.pm && <span className="pill pill-muted small">项目经理：{data.pm.name}</span>}
              {data?.labor_co && <span className="pill pill-muted small">劳务：{data.labor_co.name}</span>}
              {canManage && wf.status !== 'finalized' && (
                <ReassignControl busy={busy} currentPmId={data?.pm?.id} onReassign={(sid) => act(() => standardsWorkflowActions.reassign(wf.id, sid))} />
              )}
            </div>
            <WorkflowTimeline status={wf.status} events={data?.events} />
            {canManage && wf.status === 'submitted' && (
              <ReviewControls busy={busy} onRelease={() => act(() => standardsWorkflowActions.release(wf.id))}
                onReturn={(c) => act(() => standardsWorkflowActions.returnDraft(wf.id, c))} />
            )}
            {canManage && wf.status === 'released' && (
              <ReleasedWaitPanel
                busy={busy} releasedAt={wf.released_at} laborName={data?.labor_co?.name ?? null}
                onNudge={() => act(() => standardsWorkflowActions.nudgeLabor(wf.id))}
                onClearOnBehalf={(c) => act(() => standardsWorkflowActions.laborClearOnBehalf(wf.id, c))}
              />
            )}
            {canManage && (wf.status === 'labor_clear' || wf.status === 'revision_requested') && (
              <MeetingForm busy={busy} initialNotes={wf.meeting_notes_md} onSubmit={(b) => act(() => standardsWorkflowActions.meeting(wf.id, b))} />
            )}
            {!canManage && (wf.status === 'released' || wf.status === 'labor_clear' || wf.status === 'revision_requested') && (
              <div className="muted small" style={{ marginTop: 8 }}>
                记录确认会议、生成验收标准确认书由估价或总经理操作。/ Recording the meeting and composing the annexure is done by 估价 or the GM.
              </div>
            )}
          </div>

          <StaffDocsCard wid={wf.id} docs={data?.docs ?? []} canManage={!!canManage} frozen={wf.status === 'finalized'} busy={busy} act={act} />

          {(data?.criteria?.length ?? 0) > 0 && (
            <div className="card" style={{ marginBottom: 10 }}>
              <h4 style={{ marginTop: 0 }}>PM 选定的验收标准（{data!.criteria!.length} 条，只读）</h4>
              <div className="cmp-wrap"><table className="cmp">
                <thead><tr><th>专业</th><th>验收项目</th><th>合格标准</th><th>关键</th></tr></thead>
                <tbody>
                  {data!.criteria!.map((c) => (
                    <tr key={c.id}>
                      <td>{c.trade ?? '—'}</td>
                      <td>{c.label_zh ?? c.label_en ?? '—'}</td>
                      <td style={{ maxWidth: 420 }}><MdView md={c.pass_rule_md} className="small" /></td>
                      <td>{c.is_critical ? '⭐' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          <AnnexureSection
            wid={wf.id} finalized={wf.status === 'finalized'} busy={busy}
            canRegenerate={!!canManage && (wf.status === 'annexure_review' || wf.status === 'revision_requested')}
            onRegenerate={() => act(() => standardsWorkflowActions.regenerate(wf.id))}
          />
          {wf.status === 'finalized' && <ContractSection pid={pid} />}
        </>
      )}
    </div>
  );
}

function AssignCard({ isLocked, canManage, busy, onAssign }: { isLocked: boolean; canManage: boolean; busy: boolean; onAssign: (staffId: string) => void }) {
  const [sel, setSel] = useState('');
  if (!isLocked) {
    return <div className="card muted small">最终价锁定后（劳务确认议价结果），估价在此指派项目经理拟定验收标准。/ After the final price locks, the estimator assigns a PM here to draft the acceptance standards.</div>;
  }
  if (!canManage) return <div className="card muted small">等待估价指派项目经理。/ Waiting for the estimator to assign a PM.</div>;
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>指派项目经理 / Assign the PM</h3>
      <p className="muted small">项目经理将从标准库选定并按项目调整验收标准，经您审核后发给劳务方确认。/ The PM drafts project-specific standards from the library; you review before release to the labor company.</p>
      <PmPicker value={sel} onChange={setSel}>
        <button className="btn-primary" disabled={!sel || busy} onClick={() => onAssign(sel)}>指派 / Assign</button>
      </PmPicker>
    </div>
  );
}

/** 标准文件 — workflow attachments (过程/竣工验收标准). PM uploads from their portal; 估价/GM may add or
 *  remove here too. Downloads for all non-PM staff; frozen once finalized. */
function StaffDocsCard({ wid, docs, canManage, frozen, busy, act }: {
  wid: string; docs: PackageFileMeta[]; canManage: boolean; frozen: boolean; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('过程验收标准');
  if (docs.length === 0 && (!canManage || frozen)) return null;
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <h4 style={{ marginTop: 0 }}>标准文件 / Standards documents（{docs.length}）</h4>
      {docs.length > 0 ? (
        <ul className="small" style={{ margin: '0 0 8px', paddingLeft: 18 }}>
          {docs.map((d) => (
            <li key={d.id} style={{ marginBottom: 2 }}>
              <a href={staffWorkflowDocUrl(wid, d.id)}>{d.file_name}</a>
              {d.category && <span className="pill pill-muted small" style={{ marginLeft: 6 }}>{d.category}</span>}
              {d.uploaded_by && <span className="muted"> · {d.uploaded_by}</span>}
              {canManage && !frozen && (
                <button className="small" style={{ marginLeft: 8 }} disabled={busy}
                  onClick={() => act(() => staffDeleteWorkflowDoc(wid, d.id))}>删除 / delete</button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="muted small" style={{ marginBottom: 8 }}>项目经理或估价可上传《过程验收标准》《竣工验收标准》等文件，随标准一起发给劳务方。/ No documents yet.</div>
      )}
      {canManage && !frozen && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: 8 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>类型 / type</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="过程验收标准">过程验收标准</option>
              <option value="竣工验收标准">竣工验收标准</option>
              <option value="其他资料">其他资料 / other</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>文件 / file</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <button className="btn-primary" disabled={!file || busy}
            onClick={() => act(async () => { await staffUploadWorkflowDoc(wid, file!, category); setFile(null); })}>
            ⬆ 上传 / Upload
          </button>
        </div>
      )}
    </div>
  );
}

/** Roster picker for ASSIGNABLE site_pm rows — placeholders (共用测试账号) are excluded server-side,
 *  every option carries its 企业微信 binding state, and duplicate names get an extra warning.
 *  Shared by the initial 指派 and the header 更换 control; `children` renders inline after the select. */
function PmPicker({ value, onChange, excludeId, children }: { value: string; onChange: (v: string) => void; excludeId?: string; children?: ReactNode }) {
  const { data: pms } = useQuery<StaffRow[]>({
    queryKey: ['staff', 'site_pm', 'assignable'],
    queryFn: () => listStaff('site_pm', { excludePlaceholders: true }),
  });
  const all = (pms ?? []).filter((s) => s.active);
  // 更换 context: the CURRENT PM is not a valid target (server refuses same_pm) — drop them so a
  // one-PM roster reads as "nobody else yet", not as a picker that mysteriously won't change anyone.
  const active = excludeId ? all.filter((s) => s.id !== excludeId) : all;
  const dupeNames = new Set(active.map((s) => s.name).filter((n, i, arr) => arr.indexOf(n) !== i));
  return (
    <>
      {pms && all.length === 0 && (
        <p className="small" style={{ color: 'var(--bad)' }}>
          名册中还没有项目经理账号 — 请让项目经理先用企业微信登录一次（自动注册），或让总经理在管理页名册中添加后再指派。/
          No site-PM accounts yet — have the PM log in once via WeCom (auto-enrolls), or ask the GM to add them on the admin roster.
        </p>
      )}
      {pms && all.length > 0 && active.length === 0 && (
        <p className="small muted">
          名册中暂无其他项目经理可更换 — 新项目经理需先用企业微信登录一次（自动注册），或由总经理在名册中添加，之后即可在此更换。/
          No other PM on the roster yet — have the new PM log in once via WeCom (or the GM adds them), then reassign here.
        </p>
      )}
      {dupeNames.size > 0 && (
        <p className="small" style={{ color: 'var(--bad)' }}>
          注意：名册中存在同名项目经理，请选择标有「企业微信✓」的那行 — 否则项目经理登录后看不到这条指派。/
          Duplicate PM names on the roster — pick the row marked 企业微信✓, or the PM won't see the assignment after login.
        </p>
      )}
      <div className="row-wrap" style={{ alignItems: 'center' }}>
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— 选择项目经理 / pick a PM —</option>
          {active.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}{s.wecom_userid ? '（企业微信✓）' : '（未绑企业微信）'}{s.dept ? ` · ${s.dept}` : ''}
            </option>
          ))}
        </select>
        {children}
      </div>
    </>
  );
}

/** Header affordance: 更换项目经理 (mis-assign repair / handover) — collapsed to a small button until opened. */
function ReassignControl({ busy, currentPmId, onReassign }: { busy: boolean; currentPmId?: string; onReassign: (staffId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState('');
  if (!open) return <button type="button" className="small" disabled={busy} onClick={() => setOpen(true)}>更换项目经理 / change PM</button>;
  return (
    <div style={{ flexBasis: '100%', marginTop: 6 }}>
      <PmPicker value={sel} onChange={setSel} excludeId={currentPmId}>
        <button className="btn-primary" disabled={!sel || busy} onClick={() => { onReassign(sel); setOpen(false); setSel(''); }}>确认更换 / Reassign</button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setSel(''); }}>取消 / cancel</button>
      </PmPicker>
    </div>
  );
}

function ReviewControls({ busy, onRelease, onReturn }: { busy: boolean; onRelease: () => void; onReturn: (comment: string) => void }) {
  const [returning, setReturning] = useState(false);
  const [comment, setComment] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      {!returning ? (
        <div className="row-wrap">
          <button className="btn-primary" disabled={busy} onClick={onRelease}>✓ 通过并发劳务确认 / Release to labor</button>
          <button disabled={busy} onClick={() => setReturning(true)}>↩ 退回 PM 修改 / Return</button>
        </div>
      ) : (
        <div className="row-wrap" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
            <label>退回意见（必填）/ return comment (required)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="例如：吊顶部分请补充防火要求" />
          </div>
          <button className="btn-primary" disabled={busy || !comment.trim()} onClick={() => { onReturn(comment); setReturning(false); setComment(''); }}>确认退回 / Confirm return</button>
          <button disabled={busy} onClick={() => setReturning(false)}>×</button>
        </div>
      )}
    </div>
  );
}

/**
 * status='released' used to render NOTHING actionable: the only edge out was the labor company's own
 * 「确认清楚」 button in the portal, so when the two sides settled it offline (改改 8/7:「上午和劳务沟通
 * 过了」) the card just sat there with no compose control and no explanation of who was blocking it.
 * Mirrors the 9f5e0d3「发起投票 explains itself instead of sitting disabled」fix.
 */
function ReleasedWaitPanel({ busy, releasedAt, laborName, onNudge, onClearOnBehalf }: {
  busy: boolean; releasedAt: string | null; laborName: string | null; onNudge: () => void; onClearOnBehalf: (comment: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const days = daysSince(releasedAt);
  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--line, #e5e0d5)', paddingTop: 8 }}>
      <div className="small">
        等待{laborName ? `「${laborName}」` : '劳务方'}在门户点「确认清楚」后，才能记录确认会议、生成验收标准确认书。
        {days !== null && <> 已发出 <strong>{days}</strong> 天。</>}
        <div className="muted">Waiting for the labor company to mark the standards clear in their portal — the meeting and the annexure unlock after that.</div>
      </div>
      <div className="row-wrap" style={{ marginTop: 6 }}>
        <button disabled={busy} onClick={onNudge}>🔔 催办劳务方 / Chase</button>
        {!open && <button disabled={busy} onClick={() => setOpen(true)}>✎ 已线下确认，代确认清楚 / Record an offline confirmation</button>}
      </div>
      {open && (
        <div className="row-wrap" style={{ alignItems: 'flex-end', marginTop: 6 }}>
          <div className="field" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
            <label>确认方式（必填）/ how they confirmed (required)</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="例如：8月7日上午电话沟通，谭江平确认全部清楚" />
          </div>
          <button className="btn-primary" disabled={busy || !comment.trim()} onClick={() => { onClearOnBehalf(comment); setOpen(false); setComment(''); }}>
            确认登记 / Record
          </button>
          <button disabled={busy} onClick={() => { setOpen(false); setComment(''); }}>×</button>
        </div>
      )}
    </div>
  );
}

export function MeetingForm({ busy, initialNotes, onSubmit }: { busy: boolean; initialNotes?: string | null; onSubmit: (b: { meeting_date: string; location?: string; attendees_md?: string; notes_md: string }) => void }) {
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState('');
  const [notes, setNotes] = useState(initialNotes ?? '');
  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--line, #e5e0d5)', paddingTop: 8 }}>
      <h4 style={{ margin: '0 0 6px' }}>记录确认会议 → AI 生成验收标准确认书 / Record the meeting → compose the annexure</h4>
      <div className="row-wrap" style={{ marginBottom: 6 }}>
        <div className="field" style={{ marginBottom: 0 }}><label>会议日期（必填）/ date (required)</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="field" style={{ marginBottom: 0 }}><label>地点 / location</label><input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="项目部会议室" /></div>
        <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}><label>参会人员 / attendees</label><input value={attendees} onChange={(e) => setAttendees(e.target.value)} placeholder="王工、谭江平" /></div>
      </div>
      <div className="field"><label>会议要点（必填）/ meeting notes (required)</label>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="双方确认的要点、澄清事项、未决问题…" /></div>
      {/* the button used to sit silently dead when only 会议日期 was missing — the「点了没反应」shape
          (cf. 9f5e0d3 / fb_08cfc4e3). It now says which field is missing, and says it is working:
          the compose is a synchronous LLM call budgeted at up to 2 minutes. */}
      <button
        className="btn-primary" disabled={busy || !date || !notes.trim()}
        title={!date ? '请先填写会议日期' : !notes.trim() ? '请先填写会议要点' : '生成后双方需在系统内确认'}
        onClick={() => onSubmit({ meeting_date: date, location: location || undefined, attendees_md: attendees || undefined, notes_md: notes })}
      >
        {busy ? '生成中…（约需 1 分钟，请勿关闭页面）/ Composing…' : '生成验收标准确认书 / Compose'}
      </button>
      {!busy && (!date || !notes.trim()) && (
        <span className="muted small" style={{ marginLeft: 8 }}>
          {!date && !notes.trim() ? '请填写会议日期与会议要点' : !date ? '请填写会议日期' : '请填写会议要点'} / fill the required fields first
        </span>
      )}
    </div>
  );
}

export function AnnexureBadges({ v }: { v: AnnexureView }) {
  return (
    <span className="row-wrap small" style={{ gap: 6 }}>
      <span className={`pill small ${v.pm_confirmed_at ? 'pill-good' : 'pill-warn'}`}>
        项目经理 {v.pm_confirmed_at ? `已确认 ✓ ${v.pm_confirmed_by ?? ''}` : '待确认'}
      </span>
      <span className={`pill small ${v.labor_confirmed_at ? 'pill-good' : 'pill-warn'}`}>
        劳务单位 {v.labor_confirmed_at ? `已确认 ✓ ${v.labor_confirmed_by ?? ''}` : '待确认'}
      </span>
      {v.status === 'superseded' && <span className="pill pill-bad small">已被新版本取代</span>}
    </span>
  );
}

function AnnexureSection({ wid, finalized, busy, canRegenerate, onRegenerate }: {
  wid: string; finalized: boolean; busy: boolean; canRegenerate: boolean; onRegenerate: () => void;
}) {
  const { data, isError, error, refetch } = useQuery({ queryKey: ['workflow-annexure', wid], queryFn: () => getWorkflowAnnexure(wid) });
  const [open, setOpen] = useState(false);
  // A failed fetch used to remove the preview, the ⬇ 下载 Word link AND the ↻ 重新生成 button — every
  // route to the document at once, with nothing on screen to say so.
  if (isError) {
    return (
      <div className="card small" style={{ marginBottom: 10, borderColor: 'var(--bad)', color: 'var(--bad)' }}>
        验收标准确认书加载失败：{errText(error)}
        <button style={{ marginLeft: 8 }} onClick={() => void refetch()}>重试 / retry</button>
      </div>
    );
  }
  const v = data?.current ?? null;
  if (!v) return null;
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ alignItems: 'center' }}>
        <h4 style={{ margin: 0 }}>验收标准确认书（第 {v.version} 版）</h4>
        <AnnexureBadges v={v} />
        <span className="spacer" />
        {canRegenerate && (
          <button disabled={busy} title="按当前会议要点与验收标准重新生成新版本（双方需重新确认）" onClick={() => { if (window.confirm('重新生成会产生新版本，双方须重新确认。继续？')) onRegenerate(); }}>
            {busy ? '生成中，请稍候… / Composing…' : '↻ 重新生成'}
          </button>
        )}
        {/* a <button> nested in an <a> is invalid HTML and a classic "does nothing" in the 企业微信
            WebView; a plain download link also lets the browser surface the server's error copy */}
        <a className="btn" href={annexureDownloadUrl(v.id)}>⬇ 下载 Word / Download</a>
        <button onClick={() => setOpen(!open)}>{open ? '收起 / Hide' : '预览 / Preview'}</button>
      </div>
      {v.generated_by !== 'ai' && (
        <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>
          ⚠ 本版由会议要点直接汇编（AI 未参与，可能是当时 AI 服务不可用）— 请核对「一、会议纪要」，必要时点「↻ 重新生成」。/
          Assembled from the raw notes without the AI narrative — review section 一 and regenerate if needed.
        </div>
      )}
      {finalized && <div className="small" style={{ color: 'var(--good)', marginTop: 4 }}>✓ 双方已确认定稿 — 可生成分包合同。/ Confirmed by both parties — the contract can be generated.</div>}
      {open && <div style={{ marginTop: 8 }}><MdView md={v.content_md} className="small" /></div>}
    </div>
  );
}

/**
 * 定稿之后、合同之前的两步（改改 fb_5f47ee02, 华控）. 定标 lives on the Compare tab under a 112-row table,
 * the subcontract on the Execution tab, the contract here — and each tab only POINTED at the next, so
 * she bounced between all three and never found a button. The steps are actionable right here, where
 * she lands the moment the annexure is finalized. Price-free on purpose (PM-safe): the locked price
 * shows as a badge, never a figure.
 */
function NextSteps({ pid, canAct }: { pid: string; canAct: boolean }) {
  const qc = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  // same key PackageDetail uses, so this shares its cache and its invalidations
  const { data: pkg } = useQuery<PackageDetail>({ queryKey: ['package', pid], queryFn: () => api(`/packages/${pid}`) });
  const { data: vote } = useQuery({ queryKey: ['vote-round', pid], queryFn: () => getLatestVoteRound(pid) });
  const [busy, setBusy] = useState<'award' | 'sc' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!pkg) return null;

  const voteBlocked = voteBlocksInvite(vote);
  const bidders = pkg.engagements.filter((e) => !e.withdrawn)
    .sort((a, b) => Number(!!b.final_confirmed_at) - Number(!!a.final_confirmed_at));
  const awarded = bidders.find((e) => e.award_status === 'awarded') ?? null;
  const goTab = (tab: string) => setSearchParams((prev) => { prev.set('tab', tab); return prev; }, { replace: true });

  async function award(e: PackageDetail['engagements'][number]) {
    if (!confirm(`定标 ${e.labor_co_name}？/ Award to ${e.labor_co_name}?`)) return;
    setBusy('award'); setErr(null);
    try { await api(`/packages/${pid}/engagements/${e.id}/award`, { method: 'POST', body: '{}' }); await qc.invalidateQueries({ queryKey: ['package', pid] }); }
    catch (x) { setErr(errText(x)); } finally { setBusy(null); }
  }
  async function createSubcontract() {
    setBusy('sc'); setErr(null);
    try {
      // server defaults (改改 9/22 · Eric 9/23 — 合同模板 4.2.2–4.2.6): 中标价, 分期付款（不抵扣）— 无预付款 → 隐蔽验收后
      // 付至合同总价 50% → 全部完成验收合格后付至 80% → 竣工结算后付至结算总额 95% → 质保金 5%, 背靠背
      await execActions.generateSubcontract(pid, {});
      await Promise.all([qc.invalidateQueries({ queryKey: ['subcontracts', pid] }), qc.invalidateQueries({ queryKey: ['package', pid] })]);
    } catch (x) { setErr(errText(x)); } finally { setBusy(null); }
  }

  const Step = ({ n, done, title, children }: { n: number; done: boolean; title: ReactNode; children?: ReactNode }) => (
    <div className="row-wrap" style={{ gap: 10, alignItems: 'baseline', padding: '6px 0', borderTop: n > 1 ? '1px solid var(--rule)' : undefined }}>
      <span className={`pill small ${done ? 'pill-good' : 'pill-muted'}`} style={{ minWidth: 26, textAlign: 'center' }}>{done ? '✓' : n}</span>
      <strong style={{ minWidth: 190 }}>{title}</strong>
      <span className="row-wrap" style={{ gap: 6, alignItems: 'baseline', flex: 1 }}>{children}</span>
    </div>
  );

  return (
    <div className="card">
      <h4 style={{ margin: '0 0 4px' }}>下一步：从定稿到合同 / Next: from annexure to contract</h4>
      <p className="small muted" style={{ margin: '0 0 6px' }}>验收标准确认书已双方确认。合同要在定标并建立分包合同之后才能生成——两步都可以在这里完成。/ The annexure is confirmed. The contract can be generated once the bidder is awarded and the subcontract exists — both can be done right here.</p>
      <Step n={1} done={!!awarded} title="定标 / Award the bidder">
        {awarded
          ? <span className="small">已定标：<strong>{awarded.labor_co_name}</strong></span>
          : bidders.length === 0
            ? <span className="small muted">尚无报价方 / no bidder on this package yet</span>
            : bidders.map((e) => (
              <span key={e.id} className="row-wrap" style={{ gap: 6, alignItems: 'baseline' }}>
                <span className="small">{e.labor_co_name}</span>
                {e.final_confirmed_at && <span className="pill small pill-good" title="议价后的最终价已由劳务确认 / negotiated final price confirmed by the labor party">已锁价 / price locked</span>}
                {canAct && (
                  <button className="btn-good small" disabled={busy === 'award' || voteBlocked} onClick={() => award(e)}
                    title={voteBlocked ? '定标投票未完成，投票齐了才能定标 / voting incomplete — all votes must be in before awarding' : undefined}>
                    🏆 定标 / Award{voteBlocked ? ' · 投票未完成' : ''}
                  </button>
                )}
              </span>
            ))}
      </Step>
      <Step n={2} done={false} title="建立分包合同 / Create the subcontract">
        {!awarded
          ? <span className="small muted">先定标 / award first</span>
          : canAct
            ? <>
                <button className="btn-primary small" disabled={busy === 'sc'} onClick={createSubcontract}
                  title="合同额=中标价（不含税锁定价），按合同模板付款：无预付款 → 隐蔽工程验收后付至合同总价50% → 全部完成并验收合格后付至80% → 竣工结算后付至结算总额95% → 质保金5%，背靠背付款 / uses the locked pre-tax bid and the contract template's own clause: no advance → 50% after the concealed works pass → 80% on completion → 95% of the settlement → 5% retention, back-to-back">
                  {busy === 'sc' ? '建立中…' : '按默认条款建立 / create with defaults'}
                </button>
                <button className="small" onClick={() => goTab('execution')}>自定义条款 → 执行与结算 / custom terms</button>
              </>
            : <span className="small muted">由估价/采购/总经理建立 / 估价, 采购 or the GM creates it</span>}
      </Step>
      <Step n={3} done={false} title="生成合同 → 盖章签字 → 记录签署 / Generate, execute, record">
        <span className="small muted">建立分包合同后，这里会出现「合同生成」；打印盖章、双方签字后回来点「记录已签署」，才进入执行阶段。/ appears once the subcontract exists; record the signature after the paper is executed.</span>
      </Step>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

/**
 * 华控 9/16 · 记录已签署 carries its evidence. The bare button this replaces could be (and was) clicked
 * five seconds after generating the .docx, which marked a subcontract signed, advanced the package to
 * 执行阶段 and released an advance against a contract nobody had chopped. The scan and the date written
 * on the paper are both required before the button enables; the server re-checks everything.
 */
function RecordSignatureForm({ busy, onSubmit }: {
  busy: boolean;
  onSubmit: (body: { scan: File; signedOn: string; contractId: string }) => void | Promise<void>;
}) {
  // the browser's LOCAL date, not toISOString()'s UTC one: the office is in Beijing (UTC+8), where
  // between 00:00 and 08:00 the UTC date is still YESTERDAY — that would both default the field to the
  // wrong day and (via max) refuse the correct one. The server accepts through UTC-tomorrow, so any
  // Beijing "today" passes.
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [scan, setScan] = useState<File | null>(null);
  const [signedOn, setSignedOn] = useState(today);
  const [contractId, setContractId] = useState('');
  // 改改 9/20 (fb_3267bf3b): 「合同已下载但并未签订，就可以申请执行预付款」— she had uploaded a file that
  // was not a chopped paper. The system cannot see a chop in pixels, so the step becomes an explicit
  // attestation: the person clicking says, in words, what they are attaching. (The server call is
  // unchanged — it takes no attest field; this is the human gate in front of it.)
  const [attested, setAttested] = useState(false);
  const ready = !!scan && !!signedOn && attested;

  return (
    <div className="row-wrap small" style={{ gap: 10, alignItems: 'flex-end', marginTop: 6 }}>
      <label style={{ display: 'grid', gap: 2 }}>
        <span className="muted">签章扫描件 / executed scan <span style={{ color: 'var(--bad)' }}>*</span></span>
        <input type="file" accept=".pdf,image/*" disabled={busy}
          onChange={(e) => setScan(e.target.files?.[0] ?? null)}
          title="已盖章并双方签字的合同扫描件或照片（PDF / JPG / PNG / HEIC）/ a scan or photo of the chopped, signed contract" />
        {/* 改改 9/20 · name the file back at the uploader — the whole failure mode is attaching the wrong one */}
        {scan && <span className="muted" style={{ wordBreak: 'break-all' }}>{scan.name}{scan.size ? ` · ${fileSizeText(scan.size)}` : ''}</span>}
      </label>
      <label style={{ display: 'grid', gap: 2 }}>
        <span className="muted">签署日期 / signed on <span style={{ color: 'var(--bad)' }}>*</span></span>
        <input type="date" value={signedOn} max={today} disabled={busy}
          onChange={(e) => setSignedOn(e.target.value)}
          title="纸质合同上的签署日期，不是今天点按钮的时间 / the date written on the paper, not when you click" />
      </label>
      <label style={{ display: 'grid', gap: 2 }}>
        <span className="muted">合同编号 / contract no.（选填 / optional）</span>
        <input type="text" value={contractId} placeholder="如 / e.g. HK-2026-0916" disabled={busy}
          style={{ width: 160 }} onChange={(e) => setContractId(e.target.value)} />
      </label>
      <label className="row" style={{ gap: 6, alignItems: 'flex-start', flexBasis: '100%', marginTop: 2 }}>
        <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={attested} disabled={busy}
          onChange={(e) => setAttested(e.target.checked)} />
        <span className="muted" style={{ lineHeight: 1.45 }}>
          我确认纸质合同已由甲乙双方盖章并签字，上传的是签章后的扫描件/照片（不是系统下载的 Word/PDF）
          {' / '}I confirm both parties have chopped and signed the paper; this upload is the executed scan, not the downloaded document
          <span style={{ color: 'var(--bad)' }}> *</span>
        </span>
      </label>
      <button className="btn-primary small" disabled={busy || !ready}
        title={ready
          ? '登记纸质合同的签章证据；之后进入执行阶段（进度款/验收/结算）/ records the executed paper; opens execution'
          : !attested
            ? '需先勾选确认声明 / tick the attestation first'
            : '需先选择扫描件并填写签署日期 / attach the scan and the signing date first'}
        onClick={() => { if (scan) void onSubmit({ scan, signedOn, contractId }); }}>
        {busy ? '登记中…' : '✍ 记录已签署 / Record signature'}
      </button>
    </div>
  );
}

/**
 * 合同价（将写入合同）— 改改 9/21: 「系统里签订合同前，如有如上特殊情况，是否可以调整合同额？」 The figure the
 * generated .docx prints — the amended 合同额 once an amendment exists, else the 议价 lock (the server's contract
 * fill follows the same rule) — with the award beside it when an amendment moved it, and the amendment itself.
 * 含税 first and always the server's figure; 不含税 + 「税率未录入」 when the lock has no 税率.
 */
function ContractPriceLine({ sc }: { sc: SubcontractRow }) {
  const am = sc.value_amendment ?? null;
  const fromLock = !am && sc.award_total_rmb != null;
  const ex = fromLock ? sc.award_total_rmb ?? null : sc.value_rmb;
  const incl = fromLock ? sc.award_total_incl_tax_rmb ?? null : sc.value_incl_tax_rmb ?? null;
  return (
    <div style={{ marginTop: 8 }}>
      <div className="small" style={{ wordBreak: 'break-word' }}>
        合同价（将写入合同）/ contract price, as printed{' '}
        {incl != null
          ? <strong>含税 {rmbCents(incl)}</strong>
          : <><strong>{rmbCents(ex)}</strong><span style={{ color: 'var(--warn)' }}>（{TAX_PCT_MISSING_HINT}）</span></>}
        {am && awardDiffers(sc) && (
          <span className="muted"> · 中标价（议价锁定）{sc.award_total_incl_tax_rmb != null ? `含税 ${rmbCents(sc.award_total_incl_tax_rmb)}` : rmbCents(sc.award_total_rmb)}</span>
        )}
      </div>
      {/* the amendment explains the difference; without one, a value that drifted from the lock is shown as such */}
      {am ? <AmendmentBreakdown sc={sc} /> : <AwardVsContractLine sc={sc} />}
    </div>
  );
}

function ContractSection({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const canGenerate = ['budget', 'procurement', 'gm'].includes(me?.account?.role ?? '');
  const { data: subcontracts } = useQuery<SubcontractRow[]>({ queryKey: ['subcontracts', pid], queryFn: () => getPackageSubcontracts(pid) });
  const sc = subcontracts?.[0];
  const scId = sc?.id;
  const { data: gens } = useQuery({ queryKey: ['contract-generations', scId], queryFn: () => listContractGenerations(scId!), enabled: !!scId });
  // Multiple contract templates can coexist (e.g. 固定单价 vs 固定总价) — pick which one to use per
  // generation instead of being locked to whichever is globally "active".
  const { data: templates } = useQuery({ queryKey: ['contract-templates'], queryFn: () => listTemplatesForGeneration('contract'), enabled: canGenerate });
  const contractTemplates = templates?.templates ?? [];
  const isClean = (t: DocTemplate) => t.validation.errors.length === 0 && t.validation.missing.length === 0;
  const cleanTemplates = contractTemplates.filter(isClean);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 改改 9/21 (「调整合同额，未找到这个按钮」): once signed the amend control lives on 执行与结算 — jump there
  // instead of leaving a grey line to be missed. Declared with the other hooks, before the early return.
  const [, setSearchParams] = useSearchParams();
  const goExecution = () => setSearchParams((prev) => { prev.set('tab', 'execution'); return prev; }, { replace: true });

  // default: the active template, else the first CLEAN one — never '' while options exist (a
  // controlled <select value=""> matches no option: the browser shows one thing and submits another)
  const activeDefault = contractTemplates.find((t) => t.active)?.id ?? '';
  const selected = templateId || activeDefault || cleanTemplates[0]?.id || '';

  if (!scId) return <NextSteps pid={pid} canAct={canGenerate} />;

  async function gen() {
    setBusy(true); setErr(null);
    try { await generateContract(scId!, { templateId: selected || undefined }); await qc.invalidateQueries({ queryKey: ['contract-generations', scId] }); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }
  // The signature is recorded HERE, next to the document it refers to (fb_83b44747): the subcontract is
  // a draft until the printed contract has been chopped and signed by both parties.
  // 华控 9/16: a confirm() dialog was no evidence at all — on 9/16 someone clicked 记录已签署 five seconds
  // after generating the .docx and the package jumped to 执行阶段 on a contract nobody had signed. The
  // signature now travels with the chopped scan + the date written on it.
  async function recordSigned(body: { scan: File; signedOn: string; contractId: string }) {
    setBusy(true); setErr(null);
    try {
      await execActions.signSubcontract(scId!, body);
      await Promise.all([qc.invalidateQueries({ queryKey: ['subcontracts', pid] }), qc.invalidateQueries({ queryKey: ['package', pid] })]);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  // 改改 9/20 (fb_3267bf3b) · after 撤销签署 the subcontract is a draft again, the package is back at st.12
  // and the advance ledger is gone — refresh everything that reads any of the three.
  async function revertDone() {
    setErr(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['subcontracts', pid] }),
      qc.invalidateQueries({ queryKey: ['package', pid] }),
      qc.invalidateQueries({ queryKey: ['execution', scId] }),
    ]);
  }

  // 改改 9/21 · after 调整合同额 the next .docx prints the new figure and the plan re-prices off it — refresh
  // every key that reads the value (['wi-ledger', scId] feeds the §1.11 block on 工程指令 / 执行与结算).
  async function amendDone(result: AmendContractValueResult) {
    setErr(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['subcontracts', pid] }),
      qc.invalidateQueries({ queryKey: ['package', pid] }),
      qc.invalidateQueries({ queryKey: ['execution', scId] }),
      qc.invalidateQueries({ queryKey: ['wi-ledger', scId] }),
    ]);
    const figure = result.to.value_incl_tax_rmb != null ? `含税 ${rmbCents(result.to.value_incl_tax_rmb)}` : `不含税 ${rmbCents(result.to.value_rmb)}`;
    toast((gens?.generations ?? []).length > 0
      ? `合同额已调整 · ${figure}，请重新生成合同 Word / contract value amended — regenerate the contract to print it`
      : `合同额已调整 · ${figure}，生成合同时写入 / contract value amended — the contract will print it`, { kind: 'success', duration: 6000 });
  }

  return (
    <div className="card">
      <div className="row-wrap" style={{ alignItems: 'center' }}>
        <h4 style={{ margin: 0 }}>合同生成 / Contract generation</h4>
        <span className="spacer" />
        {canGenerate && cleanTemplates.length === 0 && contractTemplates.length > 0 && (
          <span className="small" style={{ color: 'var(--bad)' }}>
            已上传 {contractTemplates.length} 个模板但均未通过校验
            {contractTemplates[0]?.placeholders.length === 0
              ? '（文件里没有任何 {占位符} — 上传的是未标记的原始合同）'
              : `（缺少必填占位符：${[...new Set(contractTemplates.flatMap((t) => t.validation.missing))].join('、')}）`}
            — 需在 Word 中标记占位符后，由总经理在「管理→文档模板」重新上传。
            / no valid contract template — the GM must re-upload a tagged .docx (Admin → templates).
          </span>
        )}
      </div>
      {/* 改改 9/21 · the price the .docx will print, and the place to correct it BEFORE generating — above the
          button on purpose. Once signed, amending is an 执行与结算 act (it re-prices the advance + its cert). */}
      {sc && <ContractPriceLine sc={sc} />}
      {sc && sc.status !== 'signed' && <AmendContractValue sc={sc} role={me?.account?.role} disabled={busy} onDone={amendDone} />}
      {sc && sc.status === 'signed' && canAmendContractValue(me?.account?.role) && (
        <div className="row-wrap small" style={{ gap: 8, alignItems: 'center', marginTop: 4 }}>
          <span className="muted">已签署 — 合同额在「执行与结算」页按签订版调整 / signed — the contract value is amended on the Execution tab</span>
          <button className="small" onClick={goExecution}>✎ 去执行与结算调整合同额 → / Amend on the Execution tab</button>
        </div>
      )}
      {canGenerate && (
        <div className="row-wrap" style={{ alignItems: 'center', marginTop: 10 }}>
          {contractTemplates.length > 1 && cleanTemplates.length > 0 && (
            <select value={selected} onChange={(e) => setTemplateId(e.target.value)} title="选择合同模板 / choose which contract template to use">
              {contractTemplates.map((t) => (
                <option key={t.id} value={t.id} disabled={!isClean(t)}>{t.file_name}{t.active ? '（默认）' : ''}{!isClean(t) ? ' ⚠ 未通过校验' : ''}</option>
              ))}
            </select>
          )}
          <button className="btn-primary" disabled={busy || cleanTemplates.length === 0} title={cleanTemplates.length === 0 ? '暂无可用合同模板' : ''} onClick={gen}>{busy ? '生成中，请稍候… / Generating…' : '📄 生成合同 Word / Generate contract'}</button>
        </div>
      )}
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{err}</div>}
      {(gens?.generations ?? []).length > 0 && (
        <ul className="small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {gens!.generations.map((g) => (
            <li key={g.id} style={{ marginBottom: 4 }}>
              {g.created_at.slice(0, 16).replace('T', ' ')} · 验收标准确认书第 {g.annexure_version} 版 · {g.generated_by ?? ''}
              {g.fill_report.nulls.length > 0 && <span className="pill pill-warn small" style={{ marginLeft: 6 }} title={`空白字段：${g.fill_report.nulls.join(', ')}`}>⚠ {g.fill_report.nulls.length} 个空白字段</span>}
              <a href={contractDownloadUrl(g.id)} style={{ marginLeft: 8 }}>⬇ 下载 / download</a>
            </li>
          ))}
        </ul>
      )}
      {/* 改改 9/17: "合同下载后被锁定了，合同信息无法编辑" — Word opens anything downloaded from a browser in
          受保护的视图 (read-only) until 启用编辑 is clicked. Nothing in the .docx locks it; say so here rather
          than let the next person conclude the document is broken. */}
      {(gens?.generations ?? []).length > 0 && (
        <div className="muted small" style={{ marginTop: 4 }}>
          下载后若 Word 顶部提示「受保护的视图」，点「启用编辑」即可填写日期、身份证号、开户行等空白项（系统只填项目、劳务单位和合同价）。
          / If Word opens it in Protected View, click Enable Editing — the remaining blanks (dates, ID numbers, bank details) are filled by hand.
        </div>
      )}
      {(gens?.generations ?? []).length > 0 && sc && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--rule)' }}>
          {sc.status === 'signed'
            ? (
              <div className="row-wrap small" style={{ gap: 8, alignItems: 'baseline' }}>
                <span className="pill pill-good small">✓ 已签署 / signed{sc.signed_on ? ` · ${sc.signed_on}` : sc.signed_at ? ` · ${sc.signed_at.slice(0, 10)}` : ''}</span>
                {sc.contract_id && <span className="pill pill-muted small">合同编号 / no. {sc.contract_id}</span>}
                {sc.executed_file
                  ? <a href={packageFileDownloadUrl(pid, sc.executed_file.id)}>⬇ 签章扫描件 / executed scan{sc.executed_file.size_bytes ? `（${fileSizeText(sc.executed_file.size_bytes)}）` : ''}</a>
                  : <span className="muted">（签署于系统加签章扫描件之前，无扫描件存档 / signed before scans were required — no scan on file）</span>}
                {/* 改改 9/20 (fb_3267bf3b): a signature recorded against unchopped paper is undone here, not by a script. */}
                <RevertSignature scId={scId!} role={me?.account?.role} disabled={busy} onDone={revertDone} />
              </div>
            )
            : (
              <>
                <div className="muted small">下载 Word → 打印盖章 → 双方签字 → 扫描/拍照后在此登记：/ download → chop → both sign → upload the scan here:</div>
                {canGenerate
                  ? <RecordSignatureForm busy={busy} onSubmit={recordSigned} />
                  : <div className="muted small" style={{ marginTop: 4 }}>由估价/采购/总经理登记 / recorded by 估价, 采购 or the GM</div>}
              </>
            )}
        </div>
      )}
      {(gens?.generations ?? []).length === 0 && (
        <div className="muted small" style={{ marginTop: 4 }}>
          尚未生成。生成需要：验收标准确认书双方确认 ✓ + 通过校验的合同模板
          {contractTemplates.length === 0 ? '（尚未上传任何模板）' : cleanTemplates.length === 0 ? `（已上传 ${contractTemplates.length} 个，均未通过校验）` : ''}。
          / Requires a both-confirmed annexure + a validated contract template (Admin).
        </div>
      )}
    </div>
  );
}
